// SAB-642: MLB invite deep link — the beta counterpart to /nfl/join/:inviteCode.
//
// Why this exists: MLB invites were shared as /auth?invite=CODE, which Auth.tsx ignored, so
// invitees fell through to /home -> create (the same bug as SAB-642, hitting our 44 paid beta
// users). Routing MLB invites through a PUBLIC per-code page — exactly like NFL — lets the
// invite survive sign-up AND email confirmation via the proven returnTo path (SignUp bakes
// returnTo into emailRedirectTo), instead of relying on Auth.tsx's /welcome redirect or on
// router state that ProtectedRoute drops.
//
// Unauth: show the invite + a sign-up CTA carrying returnTo=/beta/join/CODE.
// Authed: reuse JoinLeaguePage (auto-looks up the code and runs the real MLB join).

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import JoinLeaguePage from '@/components/league/JoinLeaguePage';
import { UserPlus, LogIn, Loader2, Trophy } from 'lucide-react';
import sabotagelogo from '@/assets/sabotage-logo.png';

function normalizeCode(raw: string | undefined): string {
  return (raw ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export default function MLBBetaJoinPage() {
  const { code: rawCode } = useParams<{ code: string }>();
  const code = normalizeCode(rawCode);
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();

  // Best-effort league name for the unauth invite screen. Degrades to generic copy if the
  // lookup is unavailable to an anonymous caller — never blocks the sign-up CTA.
  const [leagueName, setLeagueName] = useState<string | null>(null);
  const [lookupDone, setLookupDone] = useState(false);

  useEffect(() => {
    if (user || !code) return; // authed path renders JoinLeaguePage, which looks it up itself
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.rpc('find_league_by_invite_code', { code });
        const league = Array.isArray(data) ? data[0] : data;
        if (!cancelled && league?.name) setLeagueName(league.name as string);
      } catch {
        /* anon may not be able to resolve it — fall back to generic copy */
      } finally {
        if (!cancelled) setLookupDone(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user, code]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Authed: hand off to the shared MLB join (auto-lookup + join), returning to /beta after.
  if (user) {
    return (
      <div className="min-h-screen bg-background px-4 py-8">
        <div className="max-w-[560px] mx-auto">
          <JoinLeaguePage
            inviteCode={code}
            onJoin={() => navigate('/beta')}
            onCancel={() => navigate('/beta')}
          />
        </div>
      </div>
    );
  }

  // Unauth: sign-up-led invite screen. returnTo carries the code through auth + email confirm.
  const returnTo = encodeURIComponent(`/beta/join/${code}`);
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-5">
      <div className="w-full max-w-[420px] text-center">
        <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-11 w-auto mx-auto mb-8" />
        <div className="mx-auto h-14 w-14 rounded-2xl grid place-items-center mb-5" style={{ background: 'hsl(var(--primary)/0.12)', border: '1px solid hsl(var(--primary)/0.3)' }}>
          <Trophy className="h-7 w-7 text-[hsl(var(--primary))]" />
        </div>
        <h1 className="font-display font-bold text-[22px] text-foreground">
          You're invited{leagueName ? ` to ${leagueName}` : ' to a league'}
        </h1>
        <p className="mt-2 text-[13.5px] text-muted-foreground leading-[1.5]">
          Create an account to join{leagueName ? '' : ' this MLB beta league'}. It only takes a minute.
        </p>

        <button
          type="button"
          onClick={() => navigate(`/signup?returnTo=${returnTo}`)}
          className="mt-7 h-[52px] w-full rounded-xl font-display font-bold text-[16px] text-primary-foreground bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.9)] transition-colors flex items-center justify-center gap-2"
        >
          <UserPlus className="h-4.5 w-4.5" /> Sign Up to Join
        </button>
        <p className="mt-4 text-[13px] text-muted-foreground">
          Already have an account?{' '}
          <button onClick={() => navigate(`/auth?returnTo=${returnTo}`)} className="text-primary hover:underline inline-flex items-center gap-1">
            <LogIn className="h-3.5 w-3.5" /> Sign in
          </button>
        </p>
        {!lookupDone && <p className="sr-only">Loading invite…</p>}
      </div>
    </div>
  );
}
